import { MyTripsFilter } from "@hopper-b2b/types";

/**
 * sessionStorage key used to hand off the My Trips tab to pre-select across the
 * in-app checkout -> /trips navigation (NU-251).
 *
 * The booking confirmation CTA lives in the host app's Redux store while the
 * trips screen runs in its own store, so we cannot dispatch the filter directly.
 * The `tripsFilter` URL deep-link is also unreliable for this in-app transition
 * (it can be dropped while the checkout unmounts and the trips screen mounts),
 * so the confirmation writes the intended tab here and the trips screen reads it
 * on mount. The URL deep-link is kept for external entry points (push/email).
 */
export const MY_TRIPS_INITIAL_FILTER_STORAGE_KEY =
  "hopper.myTrips.initialFilter";

const isMyTripsFilter = (value: string | null): value is MyTripsFilter =>
  !!value && Object.values(MyTripsFilter).includes(value as MyTripsFilter);

/** Persist the tab to pre-select on the next My Trips mount. */
export const writeInitialTripsFilter = (filter: MyTripsFilter): void => {
  try {
    sessionStorage.setItem(MY_TRIPS_INITIAL_FILTER_STORAGE_KEY, filter);
  } catch {
    // storage unavailable (private mode / restricted webview); the URL
    // deep-link remains as a best-effort fallback.
  }
};

/** Read and consume (clear) the stored initial filter, if any. */
export const readInitialTripsFilter = (): MyTripsFilter | undefined => {
  try {
    const stored = sessionStorage.getItem(MY_TRIPS_INITIAL_FILTER_STORAGE_KEY);
    if (isMyTripsFilter(stored)) {
      sessionStorage.removeItem(MY_TRIPS_INITIAL_FILTER_STORAGE_KEY);
      return stored;
    }
  } catch {
    // ignore unreadable storage
  }
  return undefined;
};

/**
 * Resolve the My Trips filter to select when the screen mounts.
 *
 * Priority: explicit `tripsFilter` query param (external deep-links) wins, then
 * the stored hand-off filter (in-app post-booking navigation), then a tenant
 * default. Nubank hides the `upcoming` tab via CSS, so falling back to it leaves
 * the screen with no tab selected (NU-251); Nubank therefore defaults to the
 * first visible tab (`FLIGHTS`) while every other tenant keeps the historical
 * `UPCOMING_TRIPS` default.
 */
export const resolveInitialTripsFilter = (
  queryTripsFilter: MyTripsFilter | undefined,
  isNubank: boolean,
  storedTripsFilter?: MyTripsFilter | undefined
): MyTripsFilter =>
  queryTripsFilter ??
  storedTripsFilter ??
  (isNubank ? MyTripsFilter.FLIGHTS : MyTripsFilter.UPCOMING_TRIPS);
